import React, { useState, useMemo } from "react";
import { useProject } from "@/context/ProjectContext";
import { buildDocModel, buildCombinedDocModel } from "@/lib/docModel";
import DocumentModal from "@/components/DocumentModal";
import { StatusBadge } from "@/components/StatusBadge";
import { FileText, Layers, ClipboardList, TrendingUp, ArrowRight, CheckCircle2, AlertTriangle, BookOpen, PenTool, Database, Upload } from "lucide-react";
import { toast } from "sonner";

const CARDS = [
  { type: "workbook", title: "Data Workbook", desc: "Training request form, kickoff questions, data collection and root-cause analysis from Phases 1–2.", icon: Database, tag: "Phases 1–2" },
  { type: "analysis", title: "Performance & Cause Analysis Report", desc: "Compiles HPI Phase 1 & 2 — performance gap, persona and diagnosed causes.", icon: FileText, tag: "Phases 1–2" },
  { type: "proposal", title: "Integrated Solution Proposal & HLDD", desc: "Compiles Phase 3, nested ADDIE Design/Dev, and Phase 1 business goals.", icon: Layers, tag: "Phase 3 + ADDIE" },
  { type: "design", title: "Design Document", desc: "Instructional blueprint, ARCS motivation, Kirkpatrick plan, learning transfer and rollout from Phases 3–6.", icon: PenTool, tag: "Phases 3–6" },
  { type: "implementation", title: "Implementation & Change Management Plan", desc: "Compiles Phase 4 timeline and Phase 5 communications & risk logs.", icon: ClipboardList, tag: "Phases 4–5" },
  { type: "roi", title: "Executive ROI Impact Report", desc: "Compiles Phase 1 gap data and Phase 6 results side-by-side.", icon: TrendingUp, tag: "Phase 1 vs 6" },
];

export default function PublishExport() {
  const { project, phaseData, updateMeta } = useProject();
  const [activeType, setActiveType] = useState(null);
  const [localReviews, setLocalReviews] = useState({});

  const branding = project?.branding || {};
  const brandColor = branding.brand_color || "#10B981";
  const agencyName = branding.agency_name || "Performance Flow Pro";

  const reviews = { ...(project?.reviews || {}), ...localReviews };

  const model = useMemo(() => {
    if (!activeType) return null;
    if (activeType === "deck") return buildCombinedDocModel(project, phaseData);
    return buildDocModel(activeType, project, phaseData);
  }, [activeType, project, phaseData]);

  const setBrand = (key, value) => updateMeta({ branding: { ...branding, [key]: value } });
  const onAgencyLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image file (PNG, JPG, SVG, WebP)");
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Logo must be under 1.5MB");
    const reader = new FileReader();
    reader.onload = () => setBrand("agency_logo", reader.result);
    reader.onerror = () => toast.error("Could not read the file");
    reader.readAsDataURL(file);
  };
  const onPublished = (type, data) => setLocalReviews((prev) => ({ ...prev, [type]: { ...(prev[type] || {}), ...data } }));

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600">Deliverables</p>
          <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Publishing Engine</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Compile your unified project data into client-ready documents, publish them for review, and track approval status.</p>
        </div>
        <div className="flex items-end gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Agency Name</label>
            <input data-testid="branding-name-input" value={branding.agency_name || ""} onChange={(e) => setBrand("agency_name", e.target.value)} placeholder="Your Agency" className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-emerald-500 outline-none w-44" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Brand Color</label>
            <input data-testid="branding-color-input" type="color" value={brandColor} onChange={(e) => setBrand("brand_color", e.target.value)} className="h-9 w-12 rounded border border-slate-300 cursor-pointer" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Agency Logo</label>
            <div className="flex items-center gap-2">
              {branding.agency_logo && <img src={branding.agency_logo} alt="Agency logo" data-testid="agency-logo-preview" className="h-9 w-auto max-w-[72px] object-contain border border-slate-200 rounded bg-white p-0.5" />}
              <label data-testid="upload-agency-logo-btn" className="inline-flex items-center gap-1 text-sm font-medium px-2.5 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 cursor-pointer">
                <Upload className="h-4 w-4" /> {branding.agency_logo ? "Replace" : "Upload"}
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onAgencyLogo} className="hidden" data-testid="agency-logo-input" />
              </label>
              {branding.agency_logo && <button data-testid="remove-agency-logo-btn" onClick={() => setBrand("agency_logo", "")} className="text-xs text-slate-400 hover:text-red-500">Remove</button>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mb-5">
        <div data-testid="generate-deck-banner" className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 p-5 shadow-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-11 w-11 rounded-md bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <BookOpen className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-base font-semibold text-white">Full Client Deck</h3>
              <p className="text-sm text-slate-300">Combine all six reports into a single, client-ready document — export as Markdown or PDF.</p>
            </div>
          </div>
          <button
            data-testid="generate-deck-btn"
            onClick={() => setActiveType("deck")}
            className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2 transition-colors flex-shrink-0"
          >
            Generate Full Deck <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
        {CARDS.map((c) => {
          const r = reviews[c.type] || {};
          return (
          <div key={c.type} data-testid={`export-card-${c.type}`} className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all p-6 flex flex-col">
            <div className="flex items-center justify-between">
              <div className="h-11 w-11 rounded-md bg-slate-900 flex items-center justify-center">
                <c.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <StatusBadge status={r.status} />
            </div>
            <h3 className="font-display text-lg font-semibold text-slate-900 mt-4">{c.title}</h3>
            <p className="text-sm text-slate-500 mt-1 flex-1">{c.desc}</p>
            {r.status === "Revision Needed" && r.feedback && (
              <div data-testid={`feedback-${c.type}`} className="mt-3 flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span><span className="font-semibold">Client feedback:</span> {r.feedback}</span>
              </div>
            )}
            {r.status === "Approved" && (
              <div data-testid={`verified-badge-${c.type}`} className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" /> Verified by client{r.approved_at ? ` · ${new Date(r.approved_at).toLocaleDateString()}` : ""}
              </div>
            )}
            <button
              data-testid={`generate-${c.type}`}
              onClick={() => setActiveType(c.type)}
              className="mt-5 self-start bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium text-sm flex items-center gap-2 transition-colors"
            >
              Open Document <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          );
        })}
      </div>

      {model && (
        <DocumentModal
          model={model}
          docType={activeType}
          projectId={project.id}
          brandColor={brandColor}
          agencyName={agencyName}
          review={activeType === "deck" ? undefined : reviews[activeType]}
          publishable={activeType !== "deck"}
          onPublished={onPublished}
          onClose={() => setActiveType(null)}
        />
      )}
    </div>
  );
}
